import { useMemo } from 'react';

import { makeGroupId } from '@infinex/asset-config';
import { Text } from '@infinex/ui/components';

import { AssetListItemPressable } from './AssetListItemPressable';
import { useAssetBalanceOfDifferentSources } from './hooks/useAssetBalanceOfDifferentSources';
import type { AssetSelection } from './type';

import type { CustodyDisplayFilterId } from '../../api/assets/hooks/useCustodyDisplayItems';
import { useDisabledCustodyConfig } from '../../lib/custody-config';
import {
  isSafeSourceNftUnsupported,
  SAFE_SOURCE_NFT_UNSUPPORTED,
} from '../../lib/safes/safeSourceNftUnsupported';
import { AssetIconUI } from '../crates/AssetIcon';
import { CustodyEmblemFromWalletId } from '../custody/CustodyEmblem';
import { FormattedNumber } from '../formatted-number';

export const AssetSourceSelectList = (props: {
  asset: AssetSelection;
  onSelectAssetSource: (id: CustodyDisplayFilterId) => void;
}) => {
  const { asset, onSelectAssetSource } = props;
  const assetGroupId = useMemo(
    () =>
      makeGroupId({
        assetGroupId: asset.assetGroupId,
        chain: asset.chain,
        address: asset.address,
      }),
    [asset.assetGroupId, asset.chain, asset.address]
  );

  const { sourceBalances, tokenSymbol } =
    useAssetBalanceOfDifferentSources(asset);
  const { getDisabledConfigForCustody } = useDisabledCustodyConfig();

  const tokenMeta = sourceBalances[0]?.balance;
  if (!tokenMeta) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-2 py-(--Dialog-padding)">
      <div className="flex items-center justify-between px-8 pb-2">
        <Text className="text-secondary text-base font-semibold">
          Select which wallet balance
        </Text>

        <AssetIconUI
          imgUrl={tokenMeta.imgUrl}
          symbol={tokenMeta.symbol}
          {...assetGroupId}
          chainAdornment={asset.chain}
          size="medium"
        />
      </div>

      <div className="flex flex-col px-[calc(var(--Dialog-padding)-16px)]">
        {sourceBalances.map(
          ({ custody, id, label, usdValue, tokenAmount, walletId }) => {
            // Multi-sig gating needs walletId — custody-only checks look like
            // "no members" and disable every Safe (label "Multi-sig").
            const disabledConfig =
              custody != null
                ? getDisabledConfigForCustody({ custody, walletId }, 'source')
                : null;
            // Safe sources can't quote NFT/collectible sends — block the
            // balance switch too, or a wallet-picked NFT could land on a
            // Safe balance and dead-end with no quote.
            const safeBlocksThisAsset =
              asset.assetType !== 'token' &&
              custody != null &&
              isSafeSourceNftUnsupported(custody);
            const disabled = disabledConfig != null || safeBlocksThisAsset;
            return (
              <AssetListItemPressable
                key={id}
                onPress={() => onSelectAssetSource(id)}
                isDisabled={disabled}
              >
                <div className="flex items-center gap-4">
                  <CustodyEmblemFromWalletId
                    walletId={walletId ?? 'vault'}
                    size="large"
                    isDisabled={disabled}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <Text className="text-emphasis text-left font-semibold">
                      {label}
                    </Text>
                  </div>
                </div>
                {disabled ? (
                  <Text className="text-tertiary shrink-0 text-sm">
                    {disabledConfig?.label ??
                      disabledConfig?.details ??
                      (safeBlocksThisAsset
                        ? SAFE_SOURCE_NFT_UNSUPPORTED.title
                        : 'Unavailable')}
                  </Text>
                ) : (
                  <div className="flex shrink-0 flex-col items-end">
                    <Text className="text-emphasis font-semibold">
                      $
                      <FormattedNumber
                        value={usdValue}
                        decimalPartClassName="text-secondary"
                      />
                    </Text>
                    <Text className="text-secondary text-sm">
                      <FormattedNumber value={parseFloat(tokenAmount)} />{' '}
                      {tokenSymbol ?? ''}
                    </Text>
                  </div>
                )}
              </AssetListItemPressable>
            );
          }
        )}
      </div>
    </div>
  );
};
